import { useController, useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect } from 'react';
import { CustomDetailCategory } from '@dealstack/shared/lib/generated';
import type {
	KycPlacementDraft,
	KycPlacementValue,
} from '@dealstack/frontend/app/pages/Project/MIP/MIPCreate/KYCRequirements/common/FormsTemplatesList/SignaturePlacement/kycSignaturePlacement.types.ts';

export enum EDIT_KYC_DOCUMENT_FORM_FIELDS {
	name = 'name',
	instructions = 'instructions',
	category = 'category',
	requiresSignature = 'requiresSignature',
	useManualSignaturePlacement = 'useManualSignaturePlacement',
}

const editKycDocumentFormShape = z.object({
	[EDIT_KYC_DOCUMENT_FORM_FIELDS.name]: z.string().nonempty(),
	[EDIT_KYC_DOCUMENT_FORM_FIELDS.instructions]: z.string().nullish(),
	[EDIT_KYC_DOCUMENT_FORM_FIELDS.category]: z.string().nullable().optional(),
	[EDIT_KYC_DOCUMENT_FORM_FIELDS.requiresSignature]: z.boolean(),
	[EDIT_KYC_DOCUMENT_FORM_FIELDS.useManualSignaturePlacement]: z.boolean(),
});

export type EditKycDocumentFormValues = z.infer<typeof editKycDocumentFormShape>;

export const useEditKycDocumentDrawerController = (
	formTemplateFieldKey: string,
	onClose: () => void,
	onUpdateTemplate?: (updates: {
		name?: string;
		instruction?: string;
		category?: CustomDetailCategory | null;
		requiresSignature?: boolean;
		useManualSignaturePlacement?: boolean;
		showStampMetadata?: boolean;
		pendingPlacement?: KycPlacementValue | null;
	}) => Promise<boolean>
) => {
	const formTemplateNameFieldController = useController({
		name: `${formTemplateFieldKey}.name`,
	});
	const formTemplateInstructionFieldController = useController({
		name: `${formTemplateFieldKey}.instruction`,
	});
	const formTemplateCategoryFieldController = useController({
		name: `${formTemplateFieldKey}.category`,
	});
	const formTemplateRequiresSignatureFieldController = useController({
		name: `${formTemplateFieldKey}.requiresSignature`,
	});
	const formTemplateManualPlacementFieldController = useController({
		name: `${formTemplateFieldKey}.useManualSignaturePlacement`,
	});
	// Geometry captured before the template is persisted; the wizard sends it at create time.
	const formTemplatePendingPlacementFieldController = useController({
		name: `${formTemplateFieldKey}.pendingPlacement`,
	});
	// Travels with the box: it decides the expected aspect ratio, so a compact box sent without
	// it is validated against the full-layout ratio and rejected.
	const formTemplateShowStampMetadataFieldController = useController({
		name: `${formTemplateFieldKey}.showStampMetadata`,
	});

	const editKycDocumentForm = useForm<EditKycDocumentFormValues>({
		mode: 'all',
		defaultValues: {
			[EDIT_KYC_DOCUMENT_FORM_FIELDS.name]: formTemplateNameFieldController.field.value ?? '',
			[EDIT_KYC_DOCUMENT_FORM_FIELDS.instructions]:
				formTemplateInstructionFieldController.field.value ?? '',
			[EDIT_KYC_DOCUMENT_FORM_FIELDS.category]:
				formTemplateCategoryFieldController.field.value ?? null,
			[EDIT_KYC_DOCUMENT_FORM_FIELDS.requiresSignature]:
				formTemplateRequiresSignatureFieldController.field.value ?? false,
			[EDIT_KYC_DOCUMENT_FORM_FIELDS.useManualSignaturePlacement]:
				formTemplateManualPlacementFieldController.field.value ?? false,
		},
		resolver: zodResolver(editKycDocumentFormShape),
	});

	const pendingPlacement: KycPlacementValue | null =
		formTemplatePendingPlacementFieldController.field.value ?? null;
	const showStampMetadata: boolean =
		formTemplateShowStampMetadataFieldController.field.value ?? true;

	const onSubmit = async (formValues: EditKycDocumentFormValues) => {
		// Placement only means anything on a form that gets signed.
		const useManualSignaturePlacement =
			formValues.requiresSignature && formValues.useManualSignaturePlacement;

		// A persisted form is saved first and only then reflected: writing the values back before a
		// refused save left the toggle showing a change the server never made.
		if (onUpdateTemplate) {
			const saved = await onUpdateTemplate({
				name: formValues.name,
				instruction: formValues.instructions ?? undefined,
				category: (formValues.category as CustomDetailCategory) || null,
				requiresSignature: formValues.requiresSignature,
				useManualSignaturePlacement,
				showStampMetadata,
				// Set only when the editor could not persist the box itself — enabling signing in this
				// same session is what creates the role it would have keyed against.
				pendingPlacement: useManualSignaturePlacement
					? formTemplatePendingPlacementFieldController.field.value ?? null
					: null,
			});
			if (!saved) {
				return;
			}
		}

		formTemplateNameFieldController.field.onChange(formValues.name);
		formTemplateInstructionFieldController.field.onChange(formValues.instructions);
		formTemplateCategoryFieldController.field.onChange(formValues.category);
		formTemplateRequiresSignatureFieldController.field.onChange(formValues.requiresSignature);
		formTemplateManualPlacementFieldController.field.onChange(useManualSignaturePlacement);
		if (!useManualSignaturePlacement) {
			formTemplatePendingPlacementFieldController.field.onChange(null);
		}

		onClose();
	};

	/** Store the box the editor produced; the wizard carries it into the create call. */
	const onPlacementSaved = (draft: KycPlacementDraft) => {
		formTemplateManualPlacementFieldController.field.onChange(
			draft.useManualSignaturePlacement
		);
		formTemplatePendingPlacementFieldController.field.onChange(draft.placement);
		formTemplateShowStampMetadataFieldController.field.onChange(draft.showStampMetadata);
		editKycDocumentForm.setValue(
			EDIT_KYC_DOCUMENT_FORM_FIELDS.useManualSignaturePlacement,
			draft.useManualSignaturePlacement
		);
	};

	useEffect(() => {
		return editKycDocumentForm.reset;
	}, []);

	return {
		editKycDocumentForm,
		onSubmit,
		onPlacementSaved,
		pendingPlacement,
		showStampMetadata,
	};
};
